import React, { type ReactElement, type ReactNode, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Badge,
  Box,
  ButtonSelect,
  Icon,
  type IconKind,
  TabGroup,
  TabItem,
  Tooltip,
} from '@saviynt/design-system';

import { SodHeader, SodStatusChip } from '../../../shared/components';
import {
  AAG_SOD_REPORT,
  ENTITLEMENT_DETAILS_TABS,
} from '../../../shared/constants';
import {
  useInvertedLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../../shared/hooks';
import { detailsActions } from '../data-store/detailsSlice';

import './DetailsHeader.css';

interface DetailsHeaderProps {
  params: {
    title: string;
    description: ReactNode | string | null;
    tabInAction?: string | null;
    breadcrumbItems?: {
      text: string;
      href?: string;
      onClick?: () => void;
    }[];
  };
  tabOptions: {
    label: string;
    value: string;
    icon: string;
    badgeCount?: number;
  }[];
  activeTab: string;
  setActiveTab: (value: string) => void;
  handleBackButton: () => void;
}

/**
 * DetailsHeader
 */
function DetailsHeader({
  params,
  tabOptions,
  activeTab,
  setActiveTab,
  handleBackButton,
}: DetailsHeaderProps): ReactElement {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const { navigateToUrl } = useNavigateTo();

  const { activeExports } = useTypedSelector((state) => state.aag.entReports);
  const inProgressExportsCount = activeExports.length;

  // Generate tooltip text for running exports
  const runningExportsTooltip = useMemo(() => {
    if (activeExports.length === 0) {
      return '';
    }

    const formattedTypes = activeExports.map(
      (exportItem: { reportType: string }) => {
        // Format entitlement report types for display
        if (
          exportItem.reportType === 'INHERENT_ENTITLEMENT_RISK' ||
          exportItem.reportType === 'INHERENT_ENTITLEMENT_USER'
        ) {
          return 'Entitlements with Inherent Risk';
        }

        return exportItem.reportType;
      }
    );

    const formatTypesList = (types: string[]): string => {
      // Remove duplicates
      const uniqueTypes = [...new Set(types)];

      if (uniqueTypes.length === 1) {
        return uniqueTypes[0];
      }

      if (uniqueTypes.length === 2) {
        return `${uniqueTypes[0]} and ${uniqueTypes[1]}`;
      }

      const lastType = uniqueTypes[uniqueTypes.length - 1];
      const allButLast = uniqueTypes.slice(0, -1);

      return `${allButLast.join(', ')} and ${lastType}`;
    };

    return `${l('An export is already in progress for ')} ${formatTypesList(
      formattedTypes
    )}`;
  }, [activeExports, l]);

  const handleChange = (value: string) => {
    dispatch(detailsActions.setSidePanelClose());
    setActiveTab(value);
  };

  // Check if current tab should show export button
  const shouldShowExportButton =
    activeTab === ENTITLEMENT_DETAILS_TABS.TAB_RISKS ||
    activeTab === ENTITLEMENT_DETAILS_TABS.TAB_USERS;

  const handleExportClick = () => {
    // Dispatch preferred tab to Redux state
    const preferredTab =
      activeTab === ENTITLEMENT_DETAILS_TABS.TAB_USERS
        ? 'USER_DETAILS'
        : 'RISK_DETAILS';

    dispatch(detailsActions.setPreferredReportTab(preferredTab));

    // Navigate directly to report page for entitlement context
    const reportUrl = AAG_SOD_REPORT.replace('/violations/', '/entitlement/');

    navigateToUrl(reportUrl);
  };

  const getControls = () => {
    const statusChip = (
      <SodStatusChip
        kind='medium'
        tabInAction={l(params.tabInAction ?? '')}
        className='Sod-DetailsHeader-headerChip'
      />
    );

    if (!shouldShowExportButton) {
      return statusChip;
    }

    const exportButton = (
      <ButtonSelect
        kind='icon'
        onClick={handleExportClick}
        isOpen={false}
        prefixIcon={<Icon kind='Report' />}
        BadgeComp={
          inProgressExportsCount > 0 ? (
            <Badge
              kind='number'
              color='primary'
              size='small'
              number={inProgressExportsCount}
            />
          ) : undefined
        }
      />
    );

    return (
      <Box className='Sod-DetailsHeader-controls'>
        {statusChip}
        <Box className='Sod-Violations-exportWrapper'>
          {runningExportsTooltip ? (
            <Tooltip text={runningExportsTooltip} placement='left'>
              {exportButton}
            </Tooltip>
          ) : (
            exportButton
          )}
        </Box>
      </Box>
    );
  };

  return (
    <SodHeader
      title={params.title}
      description={params.description}
      controls={getControls()}
      contentBottom={
        <TabGroup
          value={activeTab}
          onChange={handleChange}
          className='Sod-DetailsHeader-tabGroup'>
          {tabOptions.map((tab) => (
            <TabItem
              key={tab.value}
              label={l(tab.label)}
              value={tab.value}
              icon={<Icon kind={tab.icon as IconKind} size='medium' />} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
              BadgeComp={
                tab.badgeCount ? (
                  <Badge
                    kind='number'
                    size='medium'
                    badgeStyle='outlined'
                    number={tab.badgeCount}
                    color='info'
                  />
                ) : undefined
              }
            />
          ))}
        </TabGroup>
      }
      handleGoBack={handleBackButton}
      applicationIconKind='SOD'
      className='Sod-DetailsHeader'
      breadcrumbItems={params.breadcrumbItems}
    />
  );
}

export default DetailsHeader;
